// The working hours editor (4.3d.4 rebuild): the business's time zone, its opening hours, the days it opens, and per-day or
// per-department exceptions. What it does is unchanged — the same store actions, the same delayed section saves, the same
// "apply to all" warning; what changed is that it speaks the screen's language and looks like the rest of the app:
//   - day names come from the screen's language in its week order (utils/weekdays.js) — they were Arabic in an English screen,
//     cut to two letters ("ال"), because the dialog was handed the business's CALL language;
//   - every label is a word in the locale files; tokens only, logical sides only;
//   - the switch's thumb is white on the brand track (it was violet on violet, so it looked like a bare pill);
//   - a day that closes after midnight says so.
// The `lng` prop is no longer read (kept so callers need not change).
import React, { useState, useRef } from "react";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import Tab from "@mui/material/Tab";
import { Box, Typography, Switch, Divider, IconButton, Tooltip } from "@mui/material";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { RotateCcw, Moon } from "lucide-react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import TlButton from "../../../design-system/components/TlButton";
import TimeZonePicker from "../../common/TimeZonePicker";
import { dayName, orderedDays, sortDayEntries, isOvernight } from "../../../utils/weekdays";
import { setWorkingDays, setWorkingHour, setCopyOfWorkingDays, patchSection } from "./SectionsSlice";
import {
  setDefaultWorkingDays,
  setWorkingDaysHours,
  setFromDefaultWorkingHours,
  setToDefaultWorkingHours,
  applyDefaultToAllDays,
  setIsCustomWorkingDays,
  setTimezone,
  resetDayToDefault,
} from "./ProfileSlice";

const heading = {
  display: "block", mb: "var(--sp-2)", color: "var(--ink-500)", fontFamily: "var(--font-ui)",
  textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700, fontSize: "var(--t-overline)",
};
const help = { fontFamily: "var(--font-ui)", fontSize: "var(--t-caption)", color: "var(--ink-500)", lineHeight: 1.5 };
const block = { px: "var(--sp-6)", py: "var(--sp-5)" };

function DayToggle({ dayKey, selected, onClick, lang }) {
  return (
    <Box
      component="button" type="button" onClick={onClick} aria-pressed={selected}
      sx={{
        minWidth: 64, minHeight: 44, px: "var(--sp-3)", borderRadius: "var(--r-pill)", cursor: "pointer",
        border: selected ? "1.5px solid var(--brand-violet)" : "1px solid var(--line-200)",
        background: selected ? "var(--c-violet-bg)" : "var(--surface-card)",
        color: selected ? "var(--brand-violet-ink)" : "var(--ink-600)",
        fontFamily: "var(--font-ui)", fontWeight: selected ? 700 : 500, fontSize: "var(--t-body-sm)",
        transition: "background var(--dur-fast) var(--ease-standard), border-color var(--dur-fast) var(--ease-standard)",
        "&:hover": { borderColor: "var(--brand-violet)" },
        "&:focus-visible": { outline: "2px solid var(--brand-blue)", outlineOffset: 2 },
      }}
    >
      {dayName(dayKey, lang, "short")}
    </Box>
  );
}

const pickerSx = { "& .MuiInputBase-root": { fontFamily: "var(--font-ui)", fontSize: "var(--t-body-sm)", borderRadius: "var(--r-sm)", background: "var(--surface-card)" } };

function TimeRow({ label, from, to, onFromChange, onToChange, isDefault = true, onReset }) {
  const { t } = useTranslation("createplan");
  const overnight = isOvernight(from, to);
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: "var(--sp-3)", flexWrap: "wrap",
      px: "var(--sp-4)", py: "var(--sp-3)", borderRadius: "var(--r-md)",
      border: isDefault ? "1px solid var(--line-100)" : "1px solid var(--brand-violet)",
      background: "var(--surface-2)",
    }}>
      <Typography sx={{ fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: "var(--t-body-sm)", color: "var(--ink-800)", minWidth: 96 }}>{label}</Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "var(--sp-2)", flex: "1 1 280px" }}>
          <TimePicker value={dayjs(`2000-01-01T${from}`)} onChange={(v) => v && onFromChange(v.format("HH:mm"))} ampm={false}
            viewRenderers={{ hours: renderTimeViewClock, minutes: renderTimeViewClock }} slotProps={{ textField: { size: "small", sx: pickerSx, inputProps: { "aria-label": `${label} ${t("whOpens")}` } } }} />
          <Typography aria-hidden="true" sx={{ color: "var(--ink-400)" }}>–</Typography>
          <TimePicker value={dayjs(`2000-01-01T${to}`)} onChange={(v) => v && onToChange(v.format("HH:mm"))} ampm={false}
            viewRenderers={{ hours: renderTimeViewClock, minutes: renderTimeViewClock }} slotProps={{ textField: { size: "small", sx: pickerSx, inputProps: { "aria-label": `${label} ${t("whCloses")}` } } }} />
        </Box>
      </LocalizationProvider>
      {overnight && (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "var(--sp-1)", color: "var(--c-indigo)", fontFamily: "var(--font-ui)", fontSize: "var(--t-caption)", fontWeight: 600 }}>
          <Moon size={13} aria-hidden="true" /> {t("whOvernight")}
        </Box>
      )}
      {!isDefault && onReset && (
        <Tooltip title={t("whResetDay")} placement="top">
          <IconButton aria-label={t("whResetDay")} onClick={onReset} sx={{ marginInlineStart: "auto", width: 44, height: 44, color: "var(--ink-500)", "&:hover": { color: "var(--brand-violet)" } }}>
            <RotateCcw size={16} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

function DaysAndHours({ title, days, defFrom, defTo, lang, onDayToggle, onTimeChange, onResetDay }) {
  const { t } = useTranslation("createplan");
  const selected = Object.keys(days || {});
  return (
    <>
      <Box sx={block}>
        <Typography component="h3" sx={heading}>{title}</Typography>
        <Box role="group" aria-label={title} sx={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
          {orderedDays(lang).map((d) => (
            <DayToggle key={d} dayKey={d} lang={lang} selected={selected.includes(d)} onClick={() => onDayToggle(d, selected)} />
          ))}
        </Box>
      </Box>
      {selected.length > 0 && (
        <>
          <Divider sx={{ borderColor: "var(--line-100)" }} />
          <Box sx={block}>
            <Typography component="h3" sx={heading}>{t("whHoursPerDay")}</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "var(--sp-2)" }}>
              {sortDayEntries(Object.entries(days), lang).map(([day, time]) => {
                const from = time?.from || defFrom;
                const to = time?.to || defTo;
                return (
                  <TimeRow key={day} label={dayName(day, lang, "long")} from={from} to={to}
                    onFromChange={(v) => onTimeChange(day, "from", v)} onToChange={(v) => onTimeChange(day, "to", v)}
                    isDefault={from === defFrom && to === defTo} onReset={() => onResetDay(day)} />
                );
              })}
            </Box>
          </Box>
        </>
      )}
    </>
  );
}

const WHCustomizeSection = () => {
  const { t, i18n } = useTranslation("createplan");
  const lang = i18n.language;
  const [value, setValue] = useState("0");
  const sections = useSelector((state) => state.sections.sections);
  const profile = useSelector((state) => state.profile.profile);
  const dispatch = useDispatch();
  const patchTimers = useRef({});

  const schedulePatch = (name) => {
    clearTimeout(patchTimers.current[name]);
    patchTimers.current[name] = setTimeout(() => dispatch(patchSection(name)), 1200);
  };
  const scheduleAllPatches = () => { sections.forEach((s) => { if (s.dbId) schedulePatch(s.name); }); };

  const handleSwitchChange = (e) => {
    dispatch(setIsCustomWorkingDays({ value: e.target.checked }));
    // Turning per-department hours OFF copies the business's week to every department at once, so the screen and the
    // database agree.
    if (!e.target.checked && profile.workingDays) dispatch(setCopyOfWorkingDays({ workingDays: profile.workingDays }));
    scheduleAllPatches();
  };

  const handleDayToggle = (name) => (dayValue, currentSelected) => {
    const newSelected = currentSelected.includes(dayValue) ? currentSelected.filter((d) => d !== dayValue) : [...currentSelected, dayValue];
    let workingDays = {};
    if (name === "Default") workingDays = { ...profile.workingDays };
    else workingDays = { ...(sections.find((s) => s.name === name)?.workingDays || {}) };
    Object.keys(workingDays).forEach((d) => { if (!newSelected.includes(d)) delete workingDays[d]; });
    newSelected.forEach((d) => { if (!workingDays[d]) workingDays[d] = { from: profile.workingHours.from, to: profile.workingHours.to }; });
    if (name === "Default") {
      dispatch(setDefaultWorkingDays({ workingDays }));
      dispatch(setCopyOfWorkingDays({ workingDays }));
      scheduleAllPatches();
    } else {
      dispatch(setWorkingDays({ workingDays, name }));
      schedulePatch(name);
    }
  };

  const handleTimeChange = (name) => (day, pType, val) => {
    if (name === "Default") dispatch(setWorkingDaysHours({ name, day, pType, value: val }));
    else { dispatch(setWorkingHour({ name, day, pType, value: val })); schedulePatch(name); }
  };

  // The open days NOT on the default hours. The cascade spares them by design, so this is the only thing standing between
  // "I changed the opening time" and a day that quietly kept its own.
  const offDefaultDays = sortDayEntries(Object.entries(profile.workingDays || {}), lang)
    .filter(([, v]) => v && (v.from !== profile.workingHours.from || v.to !== profile.workingHours.to))
    .map(([day]) => dayName(day, lang, "long"));

  const handleResetDay = (day) => { dispatch(resetDayToDefault({ day })); scheduleAllPatches(); };
  const handleResetSectionDay = (name) => (day) => {
    dispatch(setWorkingHour({ name, day, pType: "from", value: profile.workingHours.from }));
    dispatch(setWorkingHour({ name, day, pType: "to", value: profile.workingHours.to }));
    schedulePatch(name);
  };

  return (
    <Box sx={{ minWidth: { sm: 560 } }}>
      <TabContext value={value}>
        <Box sx={{ borderBottom: "1px solid var(--line-100)", px: "var(--sp-2)" }}>
          <TabList
            onChange={(_, v) => setValue(v)} variant="scrollable" scrollButtons="auto" aria-label={t("workingHoursTitle")}
            sx={{
              "& .MuiTab-root": { fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: "var(--t-body-sm)", minWidth: 80, minHeight: 48, textTransform: "none", color: "var(--ink-500)" },
              "& .Mui-selected": { color: "var(--brand-violet-ink)" },
              "& .MuiTabs-indicator": { background: "var(--grad-brand-135)", height: 2.5, borderRadius: "var(--r-pill)" },
            }}
          >
            <Tab label={t("defaultTabLabel")} value="0" />
            {profile.isCustomWorkingDays && sections.map((s) => <Tab key={s.id} label={t(s.name)} value={`${s.id}`} />)}
          </TabList>
        </Box>

        <TabPanel value="0" sx={{ p: 0 }}>
          {/* The business's time zone: every hour below, and every time in the app, is read in it. */}
          <Box sx={block}>
            <Typography component="h3" sx={heading}>{t("whTimeZone")}</Typography>
            <TimeZonePicker id="wh-timezone" value={profile.timezone} onChange={(v) => dispatch(setTimezone({ value: v }))} />
            <Typography sx={{ ...help, mt: "var(--sp-2)" }}>{t("whTimeZoneHelp")}</Typography>
          </Box>
          <Divider sx={{ borderColor: "var(--line-100)" }} />

          <Box sx={block}>
            <Typography component="h3" sx={heading}>{t("whOpeningHours")}</Typography>
            <TimeRow label={t("whEveryDay")} from={profile.workingHours.from} to={profile.workingHours.to}
              onFromChange={(v) => { dispatch(setFromDefaultWorkingHours({ value: v })); scheduleAllPatches(); }}
              onToChange={(v) => { dispatch(setToDefaultWorkingHours({ value: v })); scheduleAllPatches(); }} />
            {offDefaultDays.length > 0 && (
              <Box sx={{ mt: "var(--sp-3)", px: "var(--sp-3)", py: "var(--sp-2)", borderRadius: "var(--r-sm)", background: "var(--warning-bg)", display: "flex", alignItems: "center", gap: "var(--sp-3)", flexWrap: "wrap" }}>
                <Typography sx={{ ...help, color: "var(--ink-800)", flex: 1, minWidth: 200 }}>{t("hoursDaysOffDefault", { days: offDefaultDays.join(", ") })}</Typography>
                <TlButton variant="secondary" size="sm" onClick={() => { dispatch(applyDefaultToAllDays()); scheduleAllPatches(); }}>{t("hoursApplyToAll")}</TlButton>
              </Box>
            )}
          </Box>
          <Divider sx={{ borderColor: "var(--line-100)" }} />

          <DaysAndHours title={t("whOpenDays")} days={profile.workingDays} defFrom={profile.workingHours.from} defTo={profile.workingHours.to} lang={lang}
            onDayToggle={handleDayToggle("Default")} onTimeChange={handleTimeChange("Default")} onResetDay={handleResetDay} />
          <Divider sx={{ borderColor: "var(--line-100)" }} />

          {/* Different hours per department */}
          <Box sx={{ ...block, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--sp-4)" }}>
            <Box>
              <Typography component="h3" sx={{ fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: "var(--t-body)", color: "var(--ink-900)" }}>{t("whPerDepartment")}</Typography>
              <Typography sx={help}>{t("whPerDepartmentDesc")}</Typography>
            </Box>
            <Switch
              checked={!!profile.isCustomWorkingDays} onChange={handleSwitchChange}
              inputProps={{ "aria-label": t("whPerDepartment") }}
              sx={{
                "& .MuiSwitch-thumb": { background: "var(--surface-card)", boxShadow: "var(--shadow-sm)" },
                "& .MuiSwitch-track": { background: "var(--line-200)", opacity: 1 },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { background: "var(--brand-violet)", opacity: 1 },
                "& .Mui-focusVisible .MuiSwitch-thumb": { outline: "2px solid var(--brand-blue)" },
              }}
            />
          </Box>
        </TabPanel>

        {profile.isCustomWorkingDays && sections.map((s) => (
          <TabPanel key={s.id} value={`${s.id}`} sx={{ p: 0 }}>
            <DaysAndHours title={t("whSectionDays", { name: t(s.name) })} days={s.workingDays || {}} defFrom={profile.workingHours.from} defTo={profile.workingHours.to} lang={lang}
              onDayToggle={handleDayToggle(s.name)} onTimeChange={handleTimeChange(s.name)} onResetDay={handleResetSectionDay(s.name)} />
          </TabPanel>
        ))}
      </TabContext>
    </Box>
  );
};

export default WHCustomizeSection;
